import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Badge, Loading, formatDate, ouvrirDocument, telechargerDocument, BackButton, niveauMessage, couleurMessage, styleMessage } from '../components/Shared';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

const STATUT_COLORS = {
  brouillon:     { bg: 'var(--bg-elevated)',        color: 'var(--text-secondary)', label: 'Brouillon' },
  validée:       { bg: 'rgba(16,185,129,0.12)',      color: 'var(--accent-emerald)', label: 'Validée' },
  exportée:      { bg: 'rgba(59,130,246,0.12)',      color: 'var(--accent-blue)',    label: 'Exportée Sage' },
  comptabilisée: { bg: 'rgba(139,92,246,0.12)',      color: '#a78bfa',              label: 'Comptabilisée' },
};

function StatutBadge({ statut }) {
  const s = STATUT_COLORS[statut] || STATUT_COLORS.brouillon;
  return (
    <span style={{ display: 'inline-block', padding: '0.25rem 0.75rem', borderRadius: '10px', fontSize: '0.8rem', fontWeight: 700, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

const inputStyle = {
  padding: '0.45rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)',
  background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.88rem',
  width: '100%', boxSizing: 'border-box',
};
const labelStyle = { fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem', display: 'block' };

function fmt(n) {
  if (n == null || n === '') return '—';
  return Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export default function PrefactureDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, hasRole } = useAuth();

  const [pf, setPf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [lignes, setLignes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [validating, setValidating] = useState(false);
  const [adjPf, setAdjPf] = useState({ prev: null, next: null });
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [docsToSend, setDocsToSend] = useState({ attestation: true, emargement: true, certificat: true });
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState('');

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  useEffect(() => {
    if (!id) return;
    fetch(`${API_BASE}/prefactures/${id}/adjacent`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setAdjPf(d)).catch(() => {});
  }, [id, token]);

  function load() {
    setLoading(true);
    fetch(`${API_BASE}/prefactures/${id}`, { headers: authHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setPf(d);
        setForm({
          periode_debut: d.periode_debut ? d.periode_debut.substring(0, 10) : '',
          periode_fin:   d.periode_fin   ? d.periode_fin.substring(0, 10)   : '',
          heures_realisees: d.heures_realisees || '',
          taux_tva: d.taux_tva || 20,
          notes: d.notes || '',
        });
        setLignes(Array.isArray(d.lignes) ? d.lignes.map(l => ({ ...l })) : []);
      })
      .catch(() => setMsg('❌ Préfacture introuvable'))
      .finally(() => setLoading(false));
  }

  useEffect(load, [id]);

  // ── Lignes helpers ─────────────────────────────────────────────
  function addLigne() {
    setLignes(prev => [...prev, { description: '', quantite: 1, unite: '', pu_ht: '', montant_ht: '' }]);
  }

  function updateLigne(i, field, value) {
    setLignes(prev => {
      const next = prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l);
      // auto-calc montant
      const row = next[i];
      if (field === 'quantite' || field === 'pu_ht') {
        const q = parseFloat(field === 'quantite' ? value : row.quantite) || 0;
        const p = parseFloat(field === 'pu_ht'    ? value : row.pu_ht)    || 0;
        next[i] = { ...next[i], montant_ht: q && p ? (q * p).toFixed(2) : next[i].montant_ht };
      }
      return next;
    });
  }

  function removeLigne(i) {
    setLignes(prev => prev.filter((_, idx) => idx !== i));
  }

  const totalHT = lignes.reduce((s, l) => s + (parseFloat(l.montant_ht) || 0), 0);
  const tva = totalHT * ((parseFloat(form.taux_tva) || 0) / 100);
  const totalTTC = totalHT + tva;

  // ── Save ───────────────────────────────────────────────────────
  async function handleSave() {
    setSaving(true);
    setMsg('');
    try {
      const body = { ...form, montant_ht: totalHT, montant_tva: tva, montant_ttc: totalTTC, lignes };
      const r = await fetch(`${API_BASE}/prefactures/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Erreur'); }
      setMsg('✓ Préfacture sauvegardée');
      setTimeout(() => setMsg(''), 3000);
      setEditing(false);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setSaving(false); }
  }

  // ── Valider ────────────────────────────────────────────────────
  async function handleValider() {
    if (!window.confirm('Valider cette préfacture ? Elle ne pourra plus être modifiée.')) return;
    setValidating(true);
    setMsg('');
    try {
      const r = await fetch(`${API_BASE}/prefactures/${id}/valider`, { method: 'POST', headers: authHeaders() });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Erreur'); }
      setMsg('✓ Préfacture validée');
      load();
      // Proposer l'envoi des documents si la préfacture est liée à une session
      if (pf?.session_id) {
        setDocsToSend({ attestation: true, emargement: true, certificat: true });
        setSendMsg('');
        setShowDocsModal(true);
      }
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setValidating(false); }
  }

  // ── Envoyer documents fin de formation (par groupe) ───────────
  async function handleEnvoyerDocs() {
    const docs = Object.entries(docsToSend).filter(([, v]) => v).map(([k]) => k);
    if (!docs.length) { setSendMsg('❌ Sélectionnez au moins un document.'); return; }
    setSending(true); setSendMsg('');
    try {
      // 1. Récupérer les groupes avec leur email
      const rg = await fetch(`${API_BASE}/sessions/${pf.session_id}/docs-envoi-groupes`, { headers: authHeaders() });
      const dg = await rg.json();
      if (!rg.ok) throw new Error(dg.error || 'Impossible de charger les groupes');
      const groupes = dg.groupes || [];
      if (!groupes.length) throw new Error('Aucun groupe trouvé pour cette session');

      // 2. Envoyer pour chaque groupe
      let envoyes = 0, erreurs = 0;
      for (const g of groupes) {
        const email = (g.email_effectif || '').trim();
        if (!email) { erreurs++; continue; }
        try {
          const r = await fetch(
            `${API_BASE}/sessions/${pf.session_id}/groupes/${g.sous_session_id}/envoyer-documents`,
            { method: 'POST', headers: authHeaders(), body: JSON.stringify({ email, docs }) }
          );
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || `Erreur ${r.status}`);
          envoyes++;
        } catch { erreurs++; }
      }
      setSendMsg(erreurs
        ? `✓ ${envoyes} envoi(s) réussi(s) — ❌ ${erreurs} groupe(s) en erreur.`
        : `✓ Documents envoyés à ${envoyes} groupe(s).`);
    } catch (e) { setSendMsg('❌ ' + e.message); }
    finally { setSending(false); }
  }

  // ── Dévalider (admin) ──────────────────────────────────────────
  async function handleDevalider() {
    if (!window.confirm('Remettre en brouillon ? (admin seulement)')) return;
    setValidating(true);
    try {
      const r = await fetch(`${API_BASE}/prefactures/${id}/devalider`, { method: 'POST', headers: authHeaders() });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || 'Erreur'); }
      setMsg('✓ Remise en brouillon');
      setTimeout(() => setMsg(''), 3000);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setValidating(false); }
  }

  // ── Export Sage (unitaire) ─────────────────────────────────────
  async function handleExportSage() {
    if (!window.confirm('Exporter cette préfacture vers Sage ? Le statut passera à "Exportée".')) return;
    try {
      const r = await fetch(`${API_BASE}/prefactures/export-sage?ids=${id}`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Erreur export');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sage_export_${pf.numero}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      load();
    } catch (e) { setMsg('❌ ' + e.message); }
  }

  // ── PDF ────────────────────────────────────────────────────────
  function handleDownloadPdf() {
    telechargerDocument({ url: `/prefactures/${id}/pdf`, nomFichier: `prefacture_${pf.numero}.pdf` });
  }

  // ── Aperçu HTML ────────────────────────────────────────────────
  function handleApercu() {
    ouvrirDocument({ titre: `Préfacture ${pf.numero}`, url: `/prefactures/${id}/html`, pdfUrl: `/prefactures/${id}/pdf`, nomFichier: `prefacture_${pf.numero}.pdf` });
  }

  if (loading) return <Loading />;
  if (!pf) return <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>{msg || 'Préfacture introuvable'}</div>;

  const isBrouillon = pf.statut === 'brouillon';
  const isValidee   = pf.statut === 'validée';
  const isLocked    = pf.statut === 'exportée' || pf.statut === 'comptabilisée';

  const displayLignes = editing ? lignes : (Array.isArray(pf.lignes) ? pf.lignes : []);
  const dispTotalHT   = editing ? totalHT : (parseFloat(pf.montant_ht) || 0);
  const dispTva       = editing ? tva     : (parseFloat(pf.montant_tva) || 0);
  const dispTTC       = editing ? totalTTC: (parseFloat(pf.montant_ttc) || 0);

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '960px' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn-secondary" onClick={() => navigate(`/prefacturations/${adjPf.prev}`)}
              disabled={!adjPf.prev} style={{ opacity: adjPf.prev ? 1 : 0.35, padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
              title={adjPf.prev || 'Première préfacture'}>◀ Précédent</button>
            <BackButton />
            <button className="btn-secondary" onClick={() => navigate(`/prefacturations/${adjPf.next}`)}
              disabled={!adjPf.next} style={{ opacity: adjPf.next ? 1 : 0.35, padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
              title={adjPf.next || 'Dernière préfacture'}>Suivant ▶</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: 'var(--accent-orange)', fontFamily: 'monospace' }}>
              {pf.numero}
            </h1>
            <StatutBadge statut={pf.statut} />
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginTop: '0.3rem' }}>
            Créée le {formatDate(pf.created_at)}
            {pf.date_validation && ` · Validée le ${formatDate(pf.date_validation)}`}
            {pf.date_export_sage && ` · Exportée le ${formatDate(pf.date_export_sage)}`}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {isBrouillon && !editing && (
            <button onClick={() => setEditing(true)}
              style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}>
              ✏ Modifier
            </button>
          )}
          {editing && (
            <>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: 'none', background: 'var(--accent-orange)', color: '#fff', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 700 }}>
                {saving ? 'Enregistrement…' : '💾 Enregistrer'}
              </button>
              <button onClick={() => { setEditing(false); load(); }}
                style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: '0.85rem', cursor: 'pointer' }}>
                Annuler
              </button>
            </>
          )}
          {isBrouillon && !editing && (
            <button onClick={handleValider} disabled={validating}
              style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: 'none', background: 'rgba(16,185,129,0.15)', color: 'var(--accent-emerald)', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 700 }}>
              ✓ Valider
            </button>
          )}
          {isValidee && hasRole('admin') && (
            <button onClick={handleDevalider} disabled={validating}
              style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-tertiary)', fontSize: '0.82rem', cursor: 'pointer' }}>
              ↩ Brouillon
            </button>
          )}
          {isValidee && pf?.session_id && (
            <button onClick={() => { setDocsToSend({ attestation: true, emargement: true, certificat: true }); setSendMsg(''); setShowDocsModal(true); }}
              style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: 'none', background: 'rgba(232,119,34,0.15)', color: 'var(--accent-orange)', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 700 }}>
              📧 Envoyer documents
            </button>
          )}
          {isValidee && (
            <button onClick={handleExportSage}
              style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: 'none', background: 'rgba(59,130,246,0.15)', color: 'var(--accent-blue)', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 700 }}>
              ⬇ Export Sage
            </button>
          )}
          <button onClick={handleApercu}
            style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: '0.85rem', cursor: 'pointer' }}>
            👁 Aperçu
          </button>
          <button onClick={handleDownloadPdf}
            style={{ padding: '0.45rem 1rem', borderRadius: '7px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: '0.85rem', cursor: 'pointer' }}>
            ⬇ PDF
          </button>
        </div>
      </div>

      {msg && (
        <div style={{ padding: '0.5rem 0.9rem', borderRadius: '7px', marginBottom: '1rem', fontSize: '0.85rem',
          ...styleMessage(niveauMessage(msg)) }}>
          {msg}
        </div>
      )}


      {/* Informations */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Bloc infos session / client */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1.2rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Session & client</h3>
          <div className="info-row"><label>Session</label>
            <Link to={`/sessions/${pf.session_id}`} style={{ color: 'var(--accent-orange)', fontWeight: 700 }}>
              {pf.session_ref || `SE-${pf.session_id}`}
            </Link>
          </div>
          {pf.formation_titre && <div className="info-row"><label>Formation</label><span>{pf.formation_titre}</span></div>}
          {pf.client_nom && <div className="info-row"><label>Client</label>
            <Link to={`/clients/${pf.client_id}`} style={{ color: 'var(--accent-blue)' }}>{pf.client_nom}</Link>
          </div>}
          {pf.code_client_sage && (
            <div className="info-row"><label>Code SAGE</label>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--accent-blue)' }}>{pf.code_client_sage}</span>
            </div>
          )}
          {pf.financeur_nom && <div className="info-row"><label>Financeur</label><span>{pf.financeur_nom}</span></div>}
          {pf.type_financeur && <div className="info-row"><label>Type</label><span>{pf.type_financeur}</span></div>}
        </div>

        {/* Bloc dates / heures */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1.2rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Période & montants</h3>
          {editing ? (
            <>
              <div style={{ marginBottom: '0.7rem' }}>
                <label style={labelStyle}>Période début</label>
                <input type="date" value={form.periode_debut} onChange={e => setForm(f => ({ ...f, periode_debut: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ marginBottom: '0.7rem' }}>
                <label style={labelStyle}>Période fin</label>
                <input type="date" value={form.periode_fin} onChange={e => setForm(f => ({ ...f, periode_fin: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ marginBottom: '0.7rem' }}>
                <label style={labelStyle}>Heures réalisées</label>
                <input type="number" value={form.heures_realisees} onChange={e => setForm(f => ({ ...f, heures_realisees: e.target.value }))} style={inputStyle} min={0} step={0.5} />
              </div>
              <div>
                <label style={labelStyle}>TVA (%)</label>
                <input type="number" value={form.taux_tva} onChange={e => setForm(f => ({ ...f, taux_tva: e.target.value }))} style={{ ...inputStyle, width: '100px' }} min={0} max={100} step={0.1} />
              </div>
            </>
          ) : (
            <>
              {(pf.periode_debut || pf.periode_fin) && (
                <div className="info-row"><label>Période</label>
                  <span>{pf.periode_debut ? formatDate(pf.periode_debut) : '—'} → {pf.periode_fin ? formatDate(pf.periode_fin) : '—'}</span>
                </div>
              )}
              {pf.heures_realisees && <div className="info-row"><label>Heures réalisées</label><span>{pf.heures_realisees} h</span></div>}
              <div className="info-row"><label>TVA</label><span>{pf.taux_tva || 20} %</span></div>
              <div className="info-row"><label>Total HT</label><span style={{ fontWeight: 700 }}>{fmt(pf.montant_ht)}</span></div>
              <div className="info-row"><label>TVA</label><span>{fmt(pf.montant_tva)}</span></div>
              <div className="info-row"><label>Total TTC</label>
                <span style={{ fontWeight: 800, color: 'var(--accent-orange)', fontSize: '1.05rem' }}>{fmt(pf.montant_ttc)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lignes de détail */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1.2rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem' }}>
          <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lignes de détail</h3>
          {editing && (
            <button onClick={addLigne}
              style={{ padding: '0.3rem 0.8rem', borderRadius: '6px', border: '1px dashed var(--border)', background: 'transparent', color: 'var(--accent-orange)', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600 }}>
              + Ligne
            </button>
          )}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: '0.78rem' }}>Description</th>
              <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: '0.78rem', width: '70px' }}>Qté</th>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: '0.78rem', width: '70px' }}>Unité</th>
              <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: '0.78rem', width: '100px' }}>PU HT</th>
              <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: '0.78rem', width: '110px' }}>Montant HT</th>
              {editing && <th style={{ width: '36px' }} />}
            </tr>
          </thead>
          <tbody>
            {displayLignes.length === 0 && (
              <tr><td colSpan={editing ? 6 : 5} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>Aucune ligne</td></tr>
            )}
            {displayLignes.map((l, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '0.35rem 0.5rem' }}>
                  {editing
                    ? <input value={l.description || ''} onChange={e => updateLigne(i, 'description', e.target.value)}
                        style={{ ...inputStyle, width: '100%' }} placeholder="Description" />
                    : l.description}
                </td>
                <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>
                  {editing
                    ? <input type="number" value={l.quantite || ''} onChange={e => updateLigne(i, 'quantite', e.target.value)}
                        style={{ ...inputStyle, width: '60px', textAlign: 'right' }} min={0} step={0.5} />
                    : l.quantite}
                </td>
                <td style={{ padding: '0.35rem 0.5rem' }}>
                  {editing
                    ? <input value={l.unite || ''} onChange={e => updateLigne(i, 'unite', e.target.value)}
                        style={{ ...inputStyle, width: '60px' }} placeholder="h / j / …" />
                    : l.unite}
                </td>
                <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>
                  {editing
                    ? <input type="number" value={l.pu_ht || ''} onChange={e => updateLigne(i, 'pu_ht', e.target.value)}
                        style={{ ...inputStyle, width: '90px', textAlign: 'right' }} min={0} step={0.01} />
                    : fmt(l.pu_ht)}
                </td>
                <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>
                  {editing
                    ? <input type="number" value={l.montant_ht || ''} onChange={e => updateLigne(i, 'montant_ht', e.target.value)}
                        style={{ ...inputStyle, width: '100px', textAlign: 'right' }} min={0} step={0.01} />
                    : fmt(l.montant_ht)}
                </td>
                {editing && (
                  <td style={{ padding: '0.35rem 0.25rem', textAlign: 'center' }}>
                    <button onClick={() => removeLigne(i)}
                      style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '1rem', padding: '0.1rem 0.3rem' }}>
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border)' }}>
              <td colSpan={editing ? 4 : 3} />
              <td style={{ padding: '0.5rem 0.5rem', textAlign: 'right', color: 'var(--text-secondary)', fontSize: '0.82rem' }}>Total HT</td>
              <td style={{ padding: '0.5rem 0.5rem', textAlign: 'right', fontWeight: 700 }}>{fmt(dispTotalHT)}</td>
              {editing && <td />}
            </tr>
            <tr>
              <td colSpan={editing ? 4 : 3} />
              <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                TVA {editing ? form.taux_tva : (pf.taux_tva || 20)} %
              </td>
              <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmt(dispTva)}</td>
              {editing && <td />}
            </tr>
            <tr style={{ borderTop: '1px solid var(--border)' }}>
              <td colSpan={editing ? 4 : 3} />
              <td style={{ padding: '0.5rem 0.5rem', textAlign: 'right', color: 'var(--text-secondary)', fontWeight: 700, fontSize: '0.9rem' }}>Total TTC</td>
              <td style={{ padding: '0.5rem 0.5rem', textAlign: 'right', fontWeight: 800, color: 'var(--accent-orange)', fontSize: '1.05rem' }}>{fmt(dispTTC)}</td>
              {editing && <td />}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Notes */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1.2rem', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.8rem', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</h3>
        {editing ? (
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={4} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Notes internes…" />
        ) : (
          <p style={{ margin: 0, color: pf.notes ? 'var(--text-primary)' : 'var(--text-tertiary)', fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>
            {pf.notes || 'Aucune note'}
          </p>
        )}
      </div>

      {/* Avertissement statut verrouillé */}
      {isLocked && (
        <div style={{ padding: '0.7rem 1rem', borderRadius: '8px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', color: 'var(--accent-blue)', fontSize: '0.84rem' }}>
          ℹ Ce document est {pf.statut === 'exportée' ? 'exporté vers Sage' : 'comptabilisé'} et ne peut plus être modifié.
        </div>
      )}

      {/* Modale — envoi documents fin de formation */}
      {showDocsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '14px', padding: '1.8rem 2rem', maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 0.4rem', color: 'var(--text-primary)', fontSize: '1.05rem' }}>Envoyer les documents de fin de formation</h3>
            <p style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', marginBottom: '1.2rem' }}>
              La préfacture est validée. Souhaitez-vous envoyer les documents au client maintenant ?
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.4rem' }}>
              {[
                { key: 'attestation', label: 'Attestation de formation', icon: '📜' },
                { key: 'emargement',  label: 'Feuille d\'émargement',    icon: '✍️' },
                { key: 'certificat',  label: 'Certificat de réalisation', icon: '🏆' },
              ].map(({ key, label, icon }) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.88rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={docsToSend[key]}
                    onChange={e => setDocsToSend(d => ({ ...d, [key]: e.target.checked }))} />
                  <span>{icon} {label}</span>
                </label>
              ))}
            </div>

            {sendMsg && (
              <p style={{ fontSize: '0.83rem', color: couleurMessage(niveauMessage(sendMsg)), marginBottom: '0.8rem', fontWeight: 600 }}>
                {sendMsg}
              </p>
            )}

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDocsModal(false)}
                style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>
                Fermer
              </button>
              <button onClick={handleEnvoyerDocs} disabled={sending}
                style={{ padding: '0.5rem 1.2rem', borderRadius: '8px', border: 'none', background: 'var(--primary)', color: '#fff', cursor: sending ? 'default' : 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                {sending ? '⏳ Envoi...' : '📧 Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
